/**
 * Centralized logging utility for the application
 * Provides consistent logging with environment-aware output
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';

  private formatMessage(level: LogLevel, message: string): string {
    const emoji = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌'
    };

    return `${emoji[level]} ${message}`;
  }

  private log(level: LogLevel, message: string, data?: any): void {

    if (this.isDevelopment) {
      const formattedMessage = this.formatMessage(level, message);

      switch (level) {
        case 'debug':
          console.log(formattedMessage, data);
          break;
        case 'info':
          console.info(formattedMessage, data);
          break;
        case 'warn':
          console.warn(formattedMessage, data);
          break;
        case 'error':
          console.error(formattedMessage, data);
          break;
      }
    } else if (level === 'error') {
      // Always log errors, even in production
      console.error(this.formatMessage(level, message), data);
      // TODO: Send to error tracking service (Sentry, etc.)
    }
  }

  debug(message: string, data?: any): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: any): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: any): void {
    this.log('warn', message, data);
  }

  error(message: string, error?: any): void {
    this.log('error', message, error);
  }

  // Specific loggers for common use cases
  subscription = {
    debug: (message: string, data?: any) => this.debug(`Subscription: ${message}`, data),
    stateChange: (state: any) => this.debug('Subscription state changed', state),
    trialStarted: () => this.info('Trial started successfully'),
    premiumActivated: () => this.info('Premium activated successfully')
  };

  auth = {
    debug: (message: string, data?: any) => this.debug(`Auth: ${message}`, data),
    error: (message: string, error?: any) => this.error(`Auth: ${message}`, error),
    signInSuccess: (user: any) => this.info('User signed in successfully', { userId: user?.id, email: user?.email }),
    signInError: (error: string) => this.error('Sign-in failed', error),
    initFailed: (error: any) => this.error('Auth initialization failed', error)
  };

  paywall = {
    triggered: (reason: string) => this.debug(`Paywall triggered: ${reason}`),
    stateChange: (isOpen: boolean) => this.debug(`Paywall state changed: ${isOpen}`)
  };
}

// Export singleton instance
export const logger = new Logger();